import {
  CustomerInfo,
  ErrorCode,
  LogLevel,
  Package,
  PackageType,
  Purchases,
  PurchasesError,
} from '@revenuecat/purchases-js';
import {
  REVENUECAT_CONFIG,
  SUBSCRIPTION_PRODUCT_IDS,
  isRevenueCatWebConfigured,
} from '../config/revenuecat';
import { SubscriptionTier } from '../types/subscription';
import { ensureRevenueCatCheckoutTarget } from '../utils/revenueCatCheckoutTarget';

export interface RevenueCatWebProduct {
  id: string;
  tier: SubscriptionTier;
  platform: 'web';
  price: number;
  currency: string;
  billingPeriod: 'monthly' | 'yearly';
  trialDays?: number;
  description: string;
  localizedPrice?: string;
  revenueCatPackage?: Package;
}

class RevenueCatWebService {
  private initialized = false;
  private currentUserId: string | null = null;
  private cachedPackages: Package[] = [];

  isAvailable(): boolean {
    return isRevenueCatWebConfigured();
  }

  async initialize(userId?: string): Promise<void> {
    if (!this.isAvailable()) return;

    const apiKey = REVENUECAT_CONFIG.apiKeyWeb;
    if (!apiKey) return;

    if (__DEV__) {
      Purchases.setLogLevel(LogLevel.Debug);
    }

    try {
      if (Purchases.isConfigured()) {
        const instance = Purchases.getSharedInstance();
        if (userId && instance.getAppUserId() !== userId) {
          await instance.changeUser(userId);
        }
        this.initialized = true;
        this.currentUserId = userId ?? instance.getAppUserId();
        return;
      }

      if (!userId) {
        console.warn(
          'RevenueCat Web: sign in required before subscriptions can be purchased'
        );
        return;
      }

      Purchases.configure({
        apiKey,
        appUserId: userId,
      });
      this.initialized = true;
      this.currentUserId = userId;
    } catch (error) {
      this.initialized = false;
      console.error('RevenueCat Web: configure failed', error);
      throw error;
    }
  }

  private getInstance(): Purchases {
    if (!Purchases.isConfigured()) {
      throw new Error('RevenueCat Web SDK is not configured');
    }
    return Purchases.getSharedInstance();
  }

  private async loadPackages(): Promise<Package[]> {
    const offerings = await this.getInstance().getOfferings();
    const offering =
      offerings.current ?? offerings.all[REVENUECAT_CONFIG.offeringId];
    const packages = offering?.availablePackages ?? [];
    this.cachedPackages = packages;
    if (__DEV__ && packages.length > 0) {
      console.log(
        'RevenueCat Web: offering packages',
        packages.map((p) => ({
          packageId: p.identifier,
          productId: p.webBillingProduct.identifier,
          checkoutUrl: p.webCheckoutURL,
        }))
      );
    }
    return packages;
  }

  private inferProductId(pkg: Package): string {
    const identifier = pkg.webBillingProduct.identifier;
    if (
      SUBSCRIPTION_PRODUCT_IDS.includes(
        identifier as (typeof SUBSCRIPTION_PRODUCT_IDS)[number]
      )
    ) {
      return identifier;
    }

    const id = identifier.toLowerCase();
    const tier: SubscriptionTier = id.includes('premium') ? 'premium' : 'standard';
    const billingPeriod =
      id.includes('yearly') || id.includes('annual') ? 'yearly' : 'monthly';
    return `${tier}_${billingPeriod}`;
  }

  private findPackage(packages: Package[], productId: string): Package | undefined {
    const exact = packages.find((pkg) => pkg.webBillingProduct.identifier === productId);
    if (exact) return exact;

  const tier = productId.includes('premium') ? 'premium' : 'standard';
    const yearly = productId.includes('yearly');

    const fuzzy = packages.find((pkg) => {
      const id = pkg.webBillingProduct.identifier.toLowerCase();
      const hasTier = tier === 'premium' ? id.includes('premium') : id.includes('standard');
      const hasPeriod = yearly
        ? id.includes('yearly') || id.includes('annual')
        : id.includes('monthly') || id.includes('month');
      return hasTier && hasPeriod;
    });
    if (fuzzy) return fuzzy;

    if (yearly) {
      return packages.find(
        (pkg) =>
          pkg.packageType === PackageType.Annual &&
          pkg.webBillingProduct.identifier.toLowerCase().includes(tier)
      );
    }
    return packages.find(
      (pkg) =>
        pkg.packageType === PackageType.Monthly &&
        pkg.webBillingProduct.identifier.toLowerCase().includes(tier)
    );
  }

  async getAvailableProducts(): Promise<RevenueCatWebProduct[]> {
    if (!this.isAvailable() || !this.initialized) return [];

    try {
      const packages = await this.loadPackages();
      if (!packages.length) return [];

      return packages.map((pkg) => this.mapPackage(pkg));
    } catch (error) {
      console.error('RevenueCat Web: getAvailableProducts failed', error);
      return [];
    }
  }

  private mapPackage(pkg: Package): RevenueCatWebProduct {
    const product = pkg.webBillingProduct;
    const productId = this.inferProductId(pkg);
    const trialDays = product.freeTrialPhase?.period
      ? periodToDays(product.freeTrialPhase.period)
      : undefined;

    return {
      id: productId,
      tier: productId.includes('premium') ? 'premium' : 'standard',
      platform: 'web',
      price: product.price.amountMicros / 1_000_000,
      currency: product.price.currency,
      billingPeriod: productId.includes('yearly') ? 'yearly' : 'monthly',
      trialDays,
      description: product.title,
      localizedPrice: product.price.formattedPrice,
      revenueCatPackage: pkg,
    };
  }

  getTierFromCustomerInfo(customerInfo: CustomerInfo | null): SubscriptionTier {
    if (!customerInfo) return 'free';
    const { entitlements } = REVENUECAT_CONFIG;
    if (customerInfo.entitlements.active[entitlements.premium]) return 'premium';
    if (customerInfo.entitlements.active[entitlements.standard]) return 'standard';
    return 'free';
  }

  getActiveProductId(customerInfo: CustomerInfo | null): string | undefined {
    if (!customerInfo) return undefined;
    const active =
      customerInfo.entitlements.active[REVENUECAT_CONFIG.entitlements.premium] ??
      customerInfo.entitlements.active[REVENUECAT_CONFIG.entitlements.standard];
    return active?.productIdentifier;
  }

  isEntitlementActive(customerInfo: CustomerInfo | null): boolean {
    return this.getTierFromCustomerInfo(customerInfo) !== 'free';
  }

  async getCustomerInfo(): Promise<CustomerInfo | null> {
    if (!this.isAvailable() || !this.initialized) return null;
    try {
      return await this.getInstance().getCustomerInfo();
    } catch (error) {
      console.error('RevenueCat Web: getCustomerInfo failed', error);
      return null;
    }
  }

  async purchaseProduct(
    productId: string,
    customerEmail?: string,
    knownPackage?: Package
  ): Promise<{
    success: boolean;
    customerInfo?: CustomerInfo;
    error?: string;
    userCancelled?: boolean;
  }> {
    if (!this.isAvailable() || !this.initialized) {
      return {
        success: false,
        error:
          'RevenueCat is not ready. Sign in and ensure EXPO_PUBLIC_REVENUECAT_WEB_API_KEY is set.',
      };
    }

    try {
      const packages =
        this.cachedPackages.length > 0 ? this.cachedPackages : await this.loadPackages();
      const pkg = knownPackage ?? this.findPackage(packages, productId);

      if (!pkg) {
        const ids = packages.map((p) => p.webBillingProduct.identifier).join(', ');
        return {
          success: false,
          error: ids
            ? `No matching web product for "${productId}". Available: ${ids}`
            : `No web products in RevenueCat offering "${REVENUECAT_CONFIG.offeringId}". Add Web Billing products in the RevenueCat dashboard.`,
        };
      }

      const htmlTarget = ensureRevenueCatCheckoutTarget();
      if (htmlTarget) {
        htmlTarget.style.pointerEvents = 'auto';
      }

      const result = await this.getInstance().purchase({
        rcPackage: pkg,
        customerEmail,
        htmlTarget,
      });

      if (htmlTarget) {
        htmlTarget.style.pointerEvents = 'none';
        htmlTarget.innerHTML = '';
      }

      return { success: true, customerInfo: result.customerInfo };
    } catch (error) {
      if (htmlTargetCleanup()) {
        /* cleared checkout overlay */
      }

      if (error instanceof PurchasesError && error.errorCode === ErrorCode.UserCancelledError) {
        return { success: false, userCancelled: true, error: 'Purchase cancelled' };
      }
      const message =
        error instanceof PurchasesError
          ? error.message
          : error instanceof Error
            ? error.message
            : 'Purchase failed';
      console.error('RevenueCat Web: purchase failed', error);
      return { success: false, error: message };
    }
  }

  async restorePurchases(): Promise<{
    success: boolean;
    customerInfo?: CustomerInfo;
    error?: string;
  }> {
    if (!this.isAvailable() || !this.initialized) {
      return { success: false, error: 'RevenueCat Web Billing is not configured' };
    }

    try {
      const customerInfo = await this.getInstance().getCustomerInfo();
      const hasActive = this.isEntitlementActive(customerInfo);
      return {
        success: hasActive,
        customerInfo,
        error: hasActive ? undefined : 'No active subscription found',
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Restore failed',
      };
    }
  }

  getManagementURL(customerInfo: CustomerInfo | null): string | undefined {
    return customerInfo?.managementURL ?? undefined;
  }
}

function htmlTargetCleanup(): boolean {
  if (typeof document === 'undefined') return false;
  const el = document.getElementById('goalmint-rc-checkout');
  if (!el) return false;
  el.style.pointerEvents = 'none';
  el.innerHTML = '';
  return true;
}

function periodToDays(period: { number: number; unit: string }): number {
  switch (period.unit) {
    case 'day':
      return period.number;
    case 'week':
      return period.number * 7;
    case 'month':
      return period.number * 30;
    case 'year':
      return period.number * 365;
    default:
      return period.number;
  }
}

export const revenueCatWebService = new RevenueCatWebService();
